import React, { useMemo, useState, useEffect, useLayoutEffect, useContext, useRef } from 'react';
import classes from './Home.module.css';
import WeatherCard from './WeatherCard';
import WeatherChip from './WeatherChip';
import { useWeatherData } from './useWeatherData';
import { auth, db } from '../../config';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { UserContext } from '../../store/user-context';
import { getItemCategories } from '../../config/helpers';

// Module-level (not a ref/state) so it survives Home unmounting/remounting
// as the user switches tabs — Root.js only keeps Home mounted while
// currentScreen === 'home', so a per-instance guard resets on every trip
// back to the Home tab and logs a fresh "session" each time. This flag only
// resets on an actual new page load, matching one log per real visit.
let sessionLoggedThisPageLoad = false;

// Icons use `fill="currentColor"` (default) rather than a `color` prop —
// wherever they're used they sit inside a container whose CSS `color` is
// set via color-mix() against --brand-primary, so the SVG just inherits it.
const NeedIcon = ({ color = 'currentColor' }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M17.7402 3.53125C17.1107 3.33094 16.4522 3.24245 15.7939 3.26562L9.23728 9.82324C9.19046 9.87008 9.16408 9.93377 9.16404 10C9.16404 10.0662 9.19053 10.1299 9.23728 10.1768L9.78025 10.7197C10.1774 11.1167 10.8226 11.1168 11.2197 10.7197L12.2197 9.71972L12.3603 9.58496C13.0816 8.93183 14.0223 8.5684 15 8.56836L15.1953 8.57324C16.1669 8.62156 17.0891 9.02866 17.7803 9.71972L20.5605 12.5C20.8748 12.0918 21.1353 11.6427 21.3301 11.1592C21.6256 10.4253 21.7666 9.6375 21.7441 8.84668C21.7215 8.05604 21.536 7.27819 21.1992 6.5625C20.9044 5.93606 20.4993 5.36809 20.0039 4.88672L19.7861 4.68554C19.191 4.16418 18.4941 3.77121 17.7402 3.53125Z" fill={color}/>
    <path d="M8.59763 3.2705C7.90696 3.19683 7.20867 3.24922 6.53806 3.42383L6.25291 3.50586C5.49632 3.74522 4.7973 4.13947 4.20017 4.66211C3.67777 5.11945 3.24251 5.66743 2.91599 6.27832L2.7822 6.54394C2.44487 7.26232 2.2602 8.04356 2.23923 8.83691C2.21834 9.6303 2.36209 10.4201 2.66111 11.1553C2.95929 11.8882 3.40633 12.552 3.97263 13.1045L11.4726 20.5322C11.7649 20.8217 12.236 20.8217 12.5283 20.5322L14.5088 18.5693L11.9697 16.0303L11.9179 15.9736C11.6777 15.6791 11.6951 15.2443 11.9697 14.9697C12.2443 14.6952 12.6791 14.6778 12.9736 14.918L13.0303 14.9697L15.5742 17.5137L17.0195 16.0801L14.4697 13.5303L14.4179 13.4736C14.1777 13.1791 14.1951 12.7443 14.4697 12.4697C14.7443 12.1952 15.1791 12.1778 15.4736 12.418L15.5303 12.4697L18.0859 15.0254L19.5322 13.5928L16.7197 10.7803C16.3207 10.3813 15.7969 10.1354 15.2402 10.0801L15 10.0684C14.4357 10.0684 13.8917 10.2646 13.459 10.6191L13.2803 10.7803L12.2803 11.7803C11.2974 12.7631 9.7026 12.7631 8.7197 11.7803L8.17673 11.2373C7.84877 10.9091 7.66404 10.4639 7.66404 10C7.66408 9.53603 7.84871 9.09083 8.17673 8.76269L12.0029 4.87402C11.7252 4.60303 11.4211 4.35958 11.0937 4.14941L10.8388 3.99511C10.1498 3.60162 9.38661 3.35475 8.59763 3.2705Z" fill={color}/>
  </svg>
);

const PackIcon = ({ color = 'currentColor' }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M20.2083 7.82141L12.5083 12.2814C12.1983 12.4614 11.8083 12.4614 11.4883 12.2814L3.78826 7.82141C3.23826 7.50141 3.09826 6.75141 3.51826 6.28141C3.80826 5.95141 4.13826 5.68141 4.48826 5.49141L9.90826 2.49141C11.0683 1.84141 12.9483 1.84141 14.1083 2.49141L19.5283 5.49141C19.8783 5.68141 20.2083 5.96141 20.4983 6.28141C20.8983 6.75141 20.7583 7.50141 20.2083 7.82141Z" fill={color}/>
    <path d="M11.4305 14.1389V20.9589C11.4305 21.7189 10.6605 22.2189 9.98047 21.8889C7.92047 20.8789 4.45047 18.9889 4.45047 18.9889C3.23047 18.2989 2.23047 16.5589 2.23047 15.1289V9.9689C2.23047 9.1789 3.06047 8.6789 3.74047 9.0689L10.9305 13.2389C11.2305 13.4289 11.4305 13.7689 11.4305 14.1389Z" fill={color}/>
    <path d="M12.5703 14.1389V20.9589C12.5703 21.7189 13.3403 22.2189 14.0203 21.8889C16.0803 20.8789 19.5503 18.9889 19.5503 18.9889C20.7703 18.2989 21.7703 16.5589 21.7703 15.1289V9.9689C21.7703 9.1789 20.9403 8.6789 20.2603 9.0689L13.0703 13.2389C12.7703 13.4289 12.5703 13.7689 12.5703 14.1389Z" fill={color}/>
  </svg>
);

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Home = ({ group, festival, festivalInfo, setCurrentScreen, setActiveCategory }) => {
  // `festival` (= group.festival) is a snapshot copied onto the group once,
  // when the event was added — it never updates again, so it goes stale the
  // moment an admin edits that festival's dates/details afterwards.
  // `festivalInfo` is the live festivals/{id} doc Root.js keeps fresh via
  // onSnapshot. Custom events (no catalog festival) have no live doc to
  // listen to, so festivalInfo stays undefined there and `festival` is
  // genuinely the only source — prefer festivalInfo, fall back to festival.
  const currentFestival = festivalInfo || festival;
  const [daysToGo, setDaysToGo] = useState();
  const [hoursToGo, setHoursToGo] = useState();
  const [minsToGo, setMinsToGo] = useState();
  const [isFestivalToday, setIsFestivalToday] = useState(false);
  // 'upcoming' | 'happening' | 'ended' | null (no start date / not yet known —
  // pill stays hidden in either case)
  const [countdownStatus, setCountdownStatus] = useState(null);
  const UserCtx = useContext(UserContext);

  // Shared between the collapsed weather chip and the expanded WeatherCard
  // below it, so the two don't each fetch forecast/climate data separately.
  const weatherData = useWeatherData(festivalInfo);
  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const weatherWidgetRef = useRef(null);

  // Collapse the expanded weather card on an outside tap.
  useEffect(() => {
    if (!weatherExpanded) return;
    const handleClickOutside = (e) => {
      if (weatherWidgetRef.current && !weatherWidgetRef.current.contains(e.target)) {
        setWeatherExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [weatherExpanded]);

  // Root.js swaps Home/My Gear/Need/Pack in and out of the same shared
  // `.content` scroll container rather than mounting a fresh scrollable
  // wrapper per screen, so without this Home opens at whatever scroll
  // position the previous screen was left at. Reapplied on the next frame
  // too — on iOS Safari a tap can leave momentum/rubber-band scrolling still
  // settling on `.content`, which can silently override a scrollTop reset
  // that only happens once, synchronously, on mount.
  useEffect(() => {
    const contentEl = document.querySelector('.content');
    if (contentEl) contentEl.scrollTop = 0;
    const raf = requestAnimationFrame(() => {
      if (contentEl) contentEl.scrollTop = 0;
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Log session
  const logSession = async (user) => {
    try {
      await addDoc(collection(db, "sessions"), {
        userid: user.id,
        username: user.name,
        festival: festivalInfo?.name || null,
        createdAt: Timestamp.fromDate(new Date())
      });
    } catch (err) {
      console.log(err.message);
    }
  };

  useEffect(() => {
    if (!UserCtx?.settings) return;
    if (sessionLoggedThisPageLoad) return;
    sessionLoggedThisPageLoad = true;
    logSession(UserCtx.settings);
  }, [UserCtx.settings?.id]);

  // Live countdown timer — always ticks every second regardless of display
  // mode; total seconds are broken down into d/h/m/s rather than left as a
  // raw seconds value. Handles festivals that have already started or
  // already ended (e.g. old/past festival docs) instead of silently
  // computing a negative countdown.
  useEffect(() => {
    if (!currentFestival?.startdate) {
      setCountdownStatus(null);
      return;
    }
    const tick = () => {
      const now = new Date();
      const start = new Date(currentFestival.startdate);
      const end = currentFestival?.enddate ? new Date(currentFestival.enddate) : null;
      const diff = start - now;

      if (diff <= 0) {
        // Start has passed — festival is either currently on, or fully over
        // if it also has a past end date. No end date means we can't prove
        // it's over, so treat it as still happening.
        const hasEnded = end && (end - now) <= 0;
        setDaysToGo(0); setHoursToGo(0); setMinsToGo(0);
        setIsFestivalToday(false);
        setCountdownStatus(hasEnded ? 'ended' : 'happening');
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      setDaysToGo(Math.floor(totalSeconds / 86400));
      setHoursToGo(Math.floor((totalSeconds % 86400) / 3600));
      setMinsToGo(Math.floor((totalSeconds % 3600) / 60));
      setIsFestivalToday(diff < 24 * 3600 * 1000 && start.toDateString() === now.toDateString());
      setCountdownStatus('upcoming');
    };
    tick();
    // Minutes is the finest unit shown, so a once-a-minute tick keeps it
    // accurate without the per-second re-renders a 1000ms interval used to
    // cause just to feed a seconds display that no longer exists.
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, [currentFestival]);

  // Item counts + preview lists
  const counts = useMemo(() => {
    if (!group?.items) return { gear: 0, need: 0, pack: 0, packed: 0, needItems: [], packItems: [] };
    const items = group.items;
    const gear = items.length;

    const needItems = items
      .filter(item => {
        const n = item?.needQuantity !== undefined
          ? item.needQuantity
          : (item?.organise || []).filter(o => o.method === 'Borrow' || o.method === 'Hire').length;
        return n > 0;
      })
      .map(item => ({
        id: item.id,
        name: item.name,
        qty: item?.needQuantity !== undefined
          ? item.needQuantity
          : (item?.organise || []).filter(o => o.method === 'Borrow' || o.method === 'Hire').length,
        isHirable: (item?.organise || []).some(o => o.method === 'Hire'),
      }));

    const packItems = items
      .filter(item => {
        // Matches PackScreen.js's own fallback exactly — an item with no
        // explicit ownQuantity and no explicit method is treated as owned
        // by default (see GearCard.js's "null/unassigned = 1 Own"
        // convention), not just ones explicitly marked 'Bring'.
        const o = item?.ownQuantity !== undefined
          ? item.ownQuantity
          : (item?.organise || []).filter(o => o.method === 'Bring' || !o.method).length;
        return o > 0;
      })
      .map(item => ({
        id: item.id,
        name: item.name,
        qty: item?.ownQuantity !== undefined
          ? item.ownQuantity
          : (item?.organise || []).filter(o => o.method === 'Bring' || !o.method).length,
        packed: !!item.packed,
      }));

    return {
      gear,
      need: needItems.length,
      pack: packItems.length,
      packed: packItems.filter(i => i.packed).length,
      needItems,
      packItems,
    };
  }, [group]);

  // Unsorted vs in-progress — sum of ownQuantity + needQuantity across every
  // item; zero means nothing has been marked Own or Need yet.
  const totalCount = group?.items?.length || 0;
  const sortedSum = (group?.items || []).reduce(
    (sum, i) => sum + (i.ownQuantity || 0) + (i.needQuantity || 0), 0
  );
  const isUnsorted = sortedSum === 0;

  const sortedCount = (group?.items || []).filter(
    i => (i.ownQuantity || 0) + (i.needQuantity || 0) > 0
  ).length;
  const progressPct = totalCount > 0 ? Math.round((sortedCount / totalCount) * 100) : 0;

  // Urgency — a fixed-amber warning state independent of brand colour,
  // deliberately not reusing sortedCount/isUnsorted above since those don't
  // carry a "how close is the festival" signal. Mirrored in BottomTabs.js
  // for the My Gear tab badge; keep the two in sync if this changes.
  const daysUntilFestival = currentFestival?.startdate
    ? Math.ceil((new Date(currentFestival.startdate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const unsortedCount = (group?.items || []).filter(
    i => (i.ownQuantity || 0) + (i.needQuantity || 0) === 0
  ).length;
  const isUrgent = daysUntilFestival !== null
    && daysUntilFestival <= 28
    && unsortedCount > 0
    && countdownStatus !== 'ended';

  let urgencyMessage = '';
  if (isUrgent) {
    const daysDisplay = Math.max(0, daysUntilFestival);
    const itemWord = unsortedCount === 1 ? 'item' : 'items';
    urgencyMessage = daysDisplay > 7
      ? `${daysDisplay} days to go — ${unsortedCount} ${itemWord} still need sorting`
      : `Festival in ${daysDisplay} days — sort your remaining ${unsortedCount} ${itemWord} now`;
  }

  // Pack card copy — counts.pack/counts.packed are computed with the exact
  // same ownQuantity/packed logic PackScreen.js itself uses (see the
  // `counts` useMemo above), so these numbers always match the Pack screen.
  let packHeading;
  let packSubtitle;
  if (counts.pack > 0 && counts.packed === counts.pack) {
    packHeading = 'All packed! 🎉';
    packSubtitle = `${counts.pack} ${counts.pack === 1 ? 'item' : 'items'} ready to go`;
  } else if (counts.packed > 0) {
    packHeading = 'Packing in progress';
    packSubtitle = `${counts.packed} of ${counts.pack} packed`;
  } else {
    packHeading = 'Pack your bag';
    packSubtitle = `${counts.pack} ${counts.pack === 1 ? 'item' : 'items'} ready to pack`;
  }

  // All categories, sorted by item count, for the progress card's chip row
  const allCategories = useMemo(() => {
    const cats = group?.categories || [];
    return cats
      .map(name => ({
        name,
        count: (group?.items || []).filter(item => getItemCategories(item).includes(name)).length,
      }))
      .sort((a, b) => b.count - a.count);
  }, [group]);

  // Measures how many chips actually fit on one row of the card (widths vary
  // with category name length, so this can't be a fixed count) and reserves
  // room for a "+N more" chip whenever there's overflow. Measurement reads
  // from an always-full, invisible copy of the row (measureRowRef) rather
  // than the visible one, so a change in categories doesn't leave the
  // visible count stuck at a stale, too-small size before it can grow back.
  const chipsRowRef = useRef(null);
  const measureRowRef = useRef(null);
  const [visibleChipCount, setVisibleChipCount] = useState(allCategories.length);
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);

  useLayoutEffect(() => {
    const container = chipsRowRef.current;
    const measureRow = measureRowRef.current;
    if (!container || !measureRow || allCategories.length === 0) return;

    const measure = () => {
      const containerWidth = container.clientWidth;
      const chipEls = Array.from(measureRow.querySelectorAll('[data-chip]'));
      const moreEl = measureRow.querySelector('[data-more-chip]');
      const gap = 8;
      const moreWidth = moreEl ? moreEl.offsetWidth + gap : 0;

      let usedWidth = 0;
      let count = 0;
      for (let i = 0; i < chipEls.length; i++) {
        const chipWidth = chipEls[i].offsetWidth;
        const withGap = i > 0 ? gap : 0;
        const hiddenAfterThis = allCategories.length - (i + 1) > 0;
        const reserve = hiddenAfterThis ? moreWidth : 0;
        if (i === 0 || usedWidth + withGap + chipWidth + reserve <= containerWidth) {
          usedWidth += withGap + chipWidth;
          count = i + 1;
        } else {
          break;
        }
      }
      setVisibleChipCount(count);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [allCategories]);

  const hiddenChipCount = allCategories.length - visibleChipCount;
  const visibleCategories = categoriesExpanded ? allCategories : allCategories.slice(0, visibleChipCount);

  const chipClassFor = (cat) => cat.count === 0
    ? classes.categoryChipEmpty
    : isUrgent
      ? classes.categoryChipUrgent
      : classes.categoryChipActive;

  return (
    <div className={classes.container}>

      <div className={classes.weatherWidget} ref={weatherWidgetRef}>
        <div className={classes.countdownRow}>
          {/* Countdown pill — hidden entirely when there's no start date at all */}
          {countdownStatus === 'upcoming' && daysToGo !== undefined && (
            <div className={classes.countdown}>
              <span className={classes.countdownText}>Until the festival: </span>
              {isFestivalToday ? (
                <span className={classes.countdownNum}>🎉 Today!</span>
              ) : (
                <>
                  <span className={classes.countdownNum}>{daysToGo}</span>
                  <span className={classes.countdownUnit}>d </span>
                  <span className={classes.countdownNum}>{hoursToGo}</span>
                  <span className={classes.countdownUnit}>h </span>
                  <span className={classes.countdownNum}>{minsToGo}</span>
                  <span className={classes.countdownUnit}>m</span>
                </>
              )}
            </div>
          )}

          {countdownStatus === 'happening' && (
            <div className={classes.countdown}>
              <span className={classes.countdownNum}>Happening now! 🎉</span>
            </div>
          )}

          {countdownStatus === 'ended' && (
            <div className={classes.countdown}>
              <span className={classes.countdownText}>Festival has ended</span>
            </div>
          )}

          <WeatherChip
            weatherData={weatherData}
            expanded={weatherExpanded}
            onToggle={() => setWeatherExpanded(v => !v)}
          />
        </div>

        {/* Expanded weather card — admin override, live forecast, or climate
            average fallback, in that priority order. Same component/content
            as before; only the collapsed trigger above it is new. */}
        <div className={`${classes.weatherExpandWrap} ${weatherExpanded ? classes.weatherExpandOpen : ''}`}>
          <WeatherCard weatherData={weatherData} />
        </div>
      </div>

      {isUnsorted ? (
        <div className={classes.section}>

          <div className={classes.heroCard}>
            <h2 className={classes.heroHeading}>Your gear list is ready</h2>
            <p className={classes.heroBody}>
              We've added {totalCount} {totalCount === 1 ? 'item' : 'items'} to get you started. Go through and mark what you own — then sort out what you still need.
            </p>
            <button className={classes.heroBtn} onClick={() => setCurrentScreen('list')}>
              Review your gear list →
            </button>
          </div>

          <div className={classes.mutedCard}>
            <div className={classes.iconCircle}><NeedIcon /></div>
            <div className={classes.actionCardBody}>
              <p className={classes.actionCardTitle}>Borrow or hire</p>
              <p className={classes.actionCardSubtitle}>Mark items as needed first</p>
            </div>
          </div>

          <div className={classes.mutedCard}>
            <div className={classes.iconCircle}><PackIcon /></div>
            <div className={classes.actionCardBody}>
              <p className={classes.actionCardTitle}>Pack your bag</p>
              <p className={classes.actionCardSubtitle}>Mark what you own first</p>
            </div>
          </div>

        </div>
      ) : (
        <div className={classes.section}>

          <div
            className={`${classes.progressCard} ${isUrgent ? classes.progressCardUrgent : ''}`}
            onClick={() => setCurrentScreen('list')}
          >
            <div className={classes.progressCardHeader}>
              <span className={classes.progressCardHeading}>Your gear list</span>
              <span className={`${classes.progressCardCount} ${isUrgent ? classes.progressCardCountUrgent : ''}`}>
                {sortedCount} of {totalCount} sorted
              </span>
            </div>
            <div className={classes.progressBarTrack}>
              <div
                className={`${classes.progressBarFill} ${isUrgent ? classes.progressBarFillUrgent : ''}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {isUrgent && (
              <p className={classes.urgencyNote}>{urgencyMessage}</p>
            )}
            {allCategories.length > 0 && (
              <>
                <div className={classes.categoryChipsRow} ref={chipsRowRef}>
                  {visibleCategories.map(cat => (
                    <button
                      key={cat.name}
                      data-chip
                      className={`${classes.categoryChip2} ${chipClassFor(cat)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveCategory && setActiveCategory(cat.name);
                        setCurrentScreen('list');
                      }}
                    >
                      {cat.name} ({cat.count})
                    </button>
                  ))}
                  {!categoriesExpanded && hiddenChipCount > 0 && (
                    <button
                      className={`${classes.categoryChip2} ${classes.categoryChipMore}`}
                      onClick={(e) => { e.stopPropagation(); setCategoriesExpanded(true); }}
                    >
                      +{hiddenChipCount} more
                    </button>
                  )}
                  {categoriesExpanded && (
                    <button
                      className={`${classes.categoryChip2} ${classes.categoryChipMore}`}
                      onClick={(e) => { e.stopPropagation(); setCategoriesExpanded(false); }}
                    >
                      Show less
                    </button>
                  )}
                </div>

                {/* Invisible full-width copy of the row, used only to measure
                    real chip widths so we know how many fit — see the
                    useLayoutEffect above. */}
                <div
                  className={classes.categoryChipsRow}
                  ref={measureRowRef}
                  aria-hidden="true"
                  style={{ position: 'absolute', visibility: 'hidden', height: 0, overflow: 'hidden', pointerEvents: 'none' }}
                >
                  {allCategories.map(cat => (
                    <button key={cat.name} data-chip className={`${classes.categoryChip2} ${chipClassFor(cat)}`}>
                      {cat.name} ({cat.count})
                    </button>
                  ))}
                  <button data-more-chip className={`${classes.categoryChip2} ${classes.categoryChipMore}`}>
                    +{allCategories.length} more
                  </button>
                </div>
              </>
            )}
          </div>

          <div className={classes.actionCard} onClick={() => setCurrentScreen('need')}>
            <div className={classes.iconCircle}><NeedIcon /></div>
            <div className={classes.actionCardBody}>
              {counts.need > 0 ? (
                <>
                  <p className={classes.actionCardTitle}>{counts.need} {counts.need === 1 ? 'item' : 'items'} to sort</p>
                  <p className={classes.actionCardSubtitle}>
                    {counts.needItems.slice(0, 3).map(i => i.name).join(', ')}
                    {counts.needItems.length > 3 ? ` and ${counts.needItems.length - 3} more` : ''}
                  </p>
                </>
              ) : (
                <p className={classes.actionCardSubtitle}>Nothing to borrow yet</p>
              )}
            </div>
            <span className={classes.chevron}><ChevronIcon /></span>
          </div>

          <div className={classes.actionCard} onClick={() => setCurrentScreen('pack')}>
            <div className={classes.iconCircle}><PackIcon /></div>
            <div className={classes.actionCardBody}>
              <p className={classes.actionCardTitle}>{packHeading}</p>
              <p className={classes.actionCardSubtitle}>{packSubtitle}</p>
            </div>
            <span className={classes.chevron}><ChevronIcon /></span>
          </div>

        </div>
      )}

    </div>
  );
};

export default Home;
